import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { useAuth } from '../hooks/useAuth';
import { getStripe } from '../services/stripe';
import { createPaymentIntent } from '../services/stripe';
import { auth } from '../services/firebase';
import PaymentForm from '../components/features/payment/PaymentForm';
import AdminBoard from '../components/features/admin/AdminBoard';
import PaymentHistory from '../components/features/payment/PaymentHistory';

/**
 * DashboardPage component
 * Main dashboard for authenticated users
 * Shows different boards based on URL parameters (payment, history, lease, admin)
 */
const DashboardPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const board = searchParams.get('board') || '';
  const { userData } = useAuth();

  const [showPaymentComponent, setShowPaymentComponent] = useState<boolean>(false);
  const [stripeClientSecret, setClientSecret] = useState<string>('');
  const [paymentCompleted, setPaymentCompleted] = useState<boolean>(false);
  const [isVisible, setIsVisible] = useState(false);
  const nodeRef = useRef(null);

  const stripePromise = getStripe();

  // Animation on mount
  useEffect(() => {
    setTimeout(() => setIsVisible(true), 100);
  }, [userData.rentPaid, paymentCompleted]);

  const dueDate = new Date();
  dueDate.setMonth(dueDate.getMonth() + 1);
  dueDate.setDate(1);

  const formattedDueDate = dueDate.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

  const payString0 = userData.rentPaid
    ? `Your rent of $${(userData.rentAmount ? (userData.rentAmount / 100).toFixed(2) : "0.00")} has been paid!`
    : `Rent payment of $${(userData.rentAmount ? (userData.rentAmount / 100).toFixed(2) : "0.00")} is due by ${formattedDueDate}`;
  const payString1 = `Payment Complete!`;

  const options = {
    clientSecret: stripeClientSecret,
  };

  const setUpPayment = async (e: React.MouseEvent<HTMLButtonElement>, amount: number | null) => {
    e.preventDefault();
    try {
      const { secret } = await createPaymentIntent(100, auth.currentUser?.email || '');
      setClientSecret(secret);
      setShowPaymentComponent(true);
    } catch (error) {
      // TODO: Add proper error handling/logging service
      console.error('Payment setup failed:', (error as Error).message || 'An unknown error occurred');
    }
  };

  const renderBoard = () => {
    switch (board) {
      case 'admin':
        return <AdminBoard />;
      case 'payment':
        return (
          <div className="dashboard-panel">
            <h2 className="dashboard-panel-title">Payment Portal</h2>
            <div className="dashboard-payment-status">
              <p>{paymentCompleted ? payString1 : payString0}</p>
              <div className="dashboard-action">
                <button
                  className="dashboard-button c-button"
                  onClick={(e) => setUpPayment(e, userData.rentAmount)}
                >
                  Make Payment
                </button>
              </div>
              {paymentCompleted && (
                <div className="payment-success-message">
                  <p>Thank you for your payment! Your receipt has been emailed to you.</p>
                </div>
              )}
            </div>
          </div>
        );
      case 'history':
        return (
          <div className="dashboard-panel">
            <PaymentHistory />
          </div>
        );
      case 'lease':
        return (
          <div className="dashboard-panel">
            <h2 className="dashboard-panel-title">Lease Information</h2>
            <div className="dashboard-content">
              <p>Your lease information will appear here.</p>
              {userData.userAddress && (
                <div className="lease-address">
                  <h3>Current Property</h3>
                  <p>{userData.userAddress}</p>
                </div>
              )}
            </div>
          </div>
        );
      default:
        return (
          <div className="dashboard-panel dashboard-welcome">
            <h2 className="dashboard-panel-title">Welcome to Your Dashboard</h2>
            <div className="dashboard-welcome-content">
              <div className="dashboard-welcome-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12,3L2,12h3v8h6v-6h2v6h6v-8h3L12,3z" />
                </svg>
              </div>
              <p className="dashboard-welcome-text">
                Welcome to your Ramirez Rentals dashboard. Use the navigation menu above to:
              </p>
              <div className="dashboard-welcome-options">
                <div className="dashboard-welcome-option">
                  <h3>Make a Payment</h3>
                  <p>View your current balance and make secure payments online.</p>
                </div>
                <div className="dashboard-welcome-option">
                  <h3>Payment History</h3>
                  <p>Review your past payments and transaction records.</p>
                </div>
                <div className="dashboard-welcome-option">
                  <h3>Lease Information</h3>
                  <p>Access details about your current lease agreement and property.</p>
                </div>
              </div>
              {userData.userAddress && (
                <div className="dashboard-welcome-address">
                  <p>Your current property: <strong>{userData.userAddress}</strong></p>
                </div>
              )}
            </div>
          </div>
        );
    }
  };

  return (
    <div className="dashboard-container">
      <div className={`dashboard-content ${isVisible ? 'content-visible' : ''}`}>
        {!showPaymentComponent ? (
          <TransitionGroup>
            <CSSTransition
              key={board}
              timeout={{
                enter: 300,
                exit: 200
              }}
              classNames="dashboard-transition"
              nodeRef={nodeRef}
            >
              <div ref={nodeRef} className="dashboard-view-container">
                {renderBoard()}
              </div>
            </CSSTransition>
          </TransitionGroup>
        ) : (
          <div className="dashboard-panel dashboard-payment-form">
            <h2 className="dashboard-panel-title">Payment Information</h2>
            <Elements stripe={stripePromise} options={options}>
              <PaymentForm
                setUser={() => { }}
                setShowPaymentComponent={setShowPaymentComponent}
                setPaymentCompleted={setPaymentCompleted}
                address={userData.userAddress}
              />
            </Elements>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
